import { TileMap, MAP_SIZE, TILE_SIZE } from "@/game/map/TileMap";
import {
  computeDecorations,
  computeLitFloors,
  DecorationPlacement,
  CrystalGem,
} from "@/game/map/DecorationPlacer";
import { FamiliarRenderer } from "./FamiliarRenderer";
import { TreasureRenderer } from "./TreasureRenderer";

/**
 * タイルマップのCSS Grid描画
 *
 * CSS: field.css の .field / .floor / .wall / .torch / .gem セクション
 *
 * タイルグリッドを生成し、装飾（たいまつ・結晶）を配置。
 * エンティティ（ゴーレム・宝箱）はタイルセル内に配置し、
 * ゴーレム移動時はセル間でDOM要素を付け替える。
 */
export class TileMapRenderer {
  private fieldElement: HTMLDivElement;
  private tileCells: HTMLDivElement[][] = [];

  constructor(
    private tileMap: TileMap,
    private stageId: number = 1
  ) {
    this.fieldElement = document.createElement("div");
    this.fieldElement.className = "field";
    this.fieldElement.style.setProperty("--cols", String(MAP_SIZE));
    this.fieldElement.style.setProperty("--rows", String(MAP_SIZE));
    this.fieldElement.style.setProperty(
      "--field-bg",
      tileMap.getThemeConfig().backgroundColor
    );

    this.buildGrid();
  }

  private buildGrid(): void {
    const tiles = this.tileMap.getTiles();
    const tc = this.tileMap.getThemeConfig();

    // 装飾計算
    const decorations = computeDecorations(tiles, MAP_SIZE, tc, this.stageId);
    const litMap = computeLitFloors(
      decorations,
      tc.decorations.torch.lightRadius,
      tiles,
      MAP_SIZE
    );

    // 装飾をマップ化（O(1)検索）
    const decoMap = new Map<string, DecorationPlacement>();
    for (const d of decorations) {
      decoMap.set(`${d.x},${d.y}`, d);
    }

    // グリッド構築
    this.tileCells = [];
    for (let y = 0; y < MAP_SIZE; y++) {
      this.tileCells[y] = [];
      for (let x = 0; x < MAP_SIZE; x++) {
        const cell = document.createElement("div");
        cell.className = "t";
        const tileType = tiles[y][x];

        if (tileType === "wall") {
          const deco = decoMap.get(`${x},${y}`);
          if (deco?.type === "torch") {
            cell.classList.add("wall", "torch");
            cell.appendChild(this.createTorch());
          } else if (deco?.type === "crystal" && deco.gems) {
            cell.classList.add("wall", "crystal");
            this.addCrystals(cell, deco.gems);
          } else {
            cell.classList.add("wall");
          }
        } else {
          cell.classList.add("floor");
          const lit = litMap.get(`${x},${y}`);
          if (lit === "center") {
            cell.classList.add("warm-center");
          } else if (lit === "lit") {
            cell.classList.add("warm-lit");
          }
        }

        this.tileCells[y][x] = cell;
        this.fieldElement.appendChild(cell);
      }
    }
  }

  private createTorch(): HTMLDivElement {
    const inner = document.createElement("div");
    inner.className = "torch-inner";

    const flame = document.createElement("div");
    flame.className = "flame";
    const stand = document.createElement("div");
    stand.className = "stand";

    inner.appendChild(flame);
    inner.appendChild(stand);
    return inner;
  }

  private addCrystals(cell: HTMLDivElement, gems: CrystalGem[]): void {
    for (const gem of gems) {
      const el = document.createElement("div");
      el.className = "gem";
      // top/left/width/height はすべてパーセント指定でタイルサイズに追従させる
      // width/height は 80px基準値を TILE_SIZE で割って正規化
      el.style.top = `${gem.top * 100}%`;
      el.style.left = `${gem.left * 100}%`;
      el.style.width = `${(gem.width / TILE_SIZE) * 100}%`;
      el.style.height = `${(gem.height / TILE_SIZE) * 100}%`;
      el.style.transform = `rotate(${gem.rotation}deg)`;
      cell.appendChild(el);
    }
  }

  /**
   * エンティティをタイルセルに配置
   */
  placeEntity(
    renderer: FamiliarRenderer | TreasureRenderer,
    gridX: number,
    gridY: number
  ): void {
    if (gridY >= 0 && gridY < MAP_SIZE && gridX >= 0 && gridX < MAP_SIZE) {
      this.tileCells[gridY][gridX].appendChild(renderer.getElement());
    }
  }

  /**
   * エンティティをタイルセル間で移動
   */
  moveEntity(
    renderer: FamiliarRenderer | TreasureRenderer,
    gridX: number,
    gridY: number
  ): void {
    // 範囲外の場合は移動しない（現在のセルに留まる）
    if (gridY < 0 || gridY >= MAP_SIZE || gridX < 0 || gridX >= MAP_SIZE) {
      return;
    }
    const el = renderer.getElement();
    el.remove();
    this.tileCells[gridY][gridX].appendChild(el);
  }

  getElement(): HTMLDivElement {
    return this.fieldElement;
  }
}
